import { createBrowserRouter, type RouteObject } from "react-router";

import { RequireSignedIn, RequireSignedOut } from "@/auth/guards";
import { NotFoundPage } from "@/components/layout/forbidden-page";
import { RouteError } from "@/components/layout/route-error";
import { PlanLayout } from "@/features/billing/plan";
import { DraftLayout } from "@/features/sales/draft";
import { TransferDraftLayout } from "@/features/transfers/draft";
import { page, publicPages } from "./routes-shared";

/**
 * Every address of the web version (guide 4.3). Each one can be opened
 * directly, shared and reloaded.
 *
 * Signed-in pages sit in the AppShell (Phase 4). Pages not built yet are
 * placeholders showing their name; the real ones arrive phase by phase, each
 * loaded only when first opened (`lazy`), so the first download stays small.
 * The signed-out pages sit in the AuthLayout (Phase 5).
 *
 * Who may open what: `area` uses the permission names of @pharmacy/domain;
 * `owners` is the admin role only.
 */

const appPages: RouteObject[] = [
  page("/", async () => (await import("@/features/home/pages/home-page")).HomePage),

  page(
    "/products",
    async () => (await import("@/features/products/pages/products-list-page")).ProductsListPage,
    "products",
  ),
  page(
    "/products/add",
    async () => (await import("@/features/products/pages/add-product-page")).AddProductPage,
    "purchases",
  ),
  page("/products/:id", async () => (await import("@/features/products/pages/product-page")).ProductPage, "products"),
  page(
    "/products/:id/edit",
    async () => (await import("@/features/products/pages/edit-product-page")).EditProductPage,
    "purchases",
  ),
  page("/expiry", async () => (await import("@/features/products/pages/expiry-page")).ExpiryPage, "products"),
  page(
    "/out-of-stock",
    async () => (await import("@/features/products/pages/out-of-stock-page")).OutOfStockPage,
    "products",
  ),
  page(
    "/needs-attention",
    async () => (await import("@/features/products/pages/attention-page")).AttentionPage,
    "owners",
  ),

  page("/sales", async () => (await import("@/features/sales/pages/sales-list-page")).SalesListPage, "pos"),
  page("/sales/new", async () => (await import("@/features/sales/pages/add-sale-page")).AddSalePage, "pos"),
  page("/sales/check", async () => (await import("@/features/sales/pages/check-sale-page")).CheckSalePage, "pos"),
  // The receipt has no ←: the sale is finished (guide 8.5).
  page("/sales/:id/receipt", async () => (await import("@/features/sales/pages/receipt-page")).ReceiptPage, "pos"),
  page("/sales/:id", async () => (await import("@/features/sales/pages/sale-page")).SalePage, "pos"),

  page("/credits", async () => (await import("@/features/credits/pages/credits-list-page")).CreditsListPage, "pos"),
  page("/credits/new", async () => (await import("@/features/credits/pages/add-credit-page")).AddCreditPage, "pos"),
  page("/credits/:key", async () => (await import("@/features/credits/pages/customer-page")).CustomerPage, "pos"),
  page(
    "/credits/:key/statement",
    async () => (await import("@/features/credits/pages/statement-page")).StatementPage,
    "pos",
  ),

  page("/reports", async () => (await import("@/features/reports/pages/reports-page")).ReportsPage, "reports"),
  page(
    "/reports/most-sold",
    async () => (await import("@/features/reports/pages/most-sold-page")).MostSoldPage,
    "reports",
  ),
  page(
    "/reports/dead-stock",
    async () => (await import("@/features/reports/pages/dead-stock-page")).DeadStockPage,
    "reports",
  ),
  page(
    "/reports/activity",
    async () => (await import("@/features/reports/pages/activity-page")).ActivityPage,
    "owners",
  ),

  page(
    "/transfers",
    async () => (await import("@/features/transfers/pages/transfers-list-page")).TransfersListPage,
    "transfers",
  ),
  page("/transfers/send", async () => (await import("@/features/transfers/pages/send-page")).SendPage, "transfers"),
  page(
    "/transfers/review",
    async () => (await import("@/features/transfers/pages/review-page")).ReviewPage,
    "transfers",
  ),
  page(
    "/transfers/:id",
    async () => (await import("@/features/transfers/pages/transfer-page")).TransferPage,
    "transfers",
  ),

  page("/staff", async () => (await import("@/features/staff/pages/staff-list-page")).StaffListPage, "owners"),
  page("/staff/new", async () => (await import("@/features/staff/pages/add-staff-page")).AddStaffPage, "owners"),
  page(
    "/staff/invite-owner",
    async () => (await import("@/features/staff/pages/invite-owner-page")).InviteOwnerPage,
    "owners",
  ),
  page("/staff/:id", async () => (await import("@/features/staff/pages/staff-member-page")).StaffMemberPage, "owners"),

  page("/suppliers", async () => (await import("@/features/settings/pages/suppliers-page")).SuppliersPage, "purchases"),
  page("/branches", async () => (await import("@/features/settings/pages/branches-page")).BranchesPage, "owners"),

  page("/settings", async () => (await import("@/features/settings/pages/settings-page")).SettingsPage),
  page("/settings/profile", async () => (await import("@/features/settings/pages/profile-page")).ProfilePage),
  page(
    "/settings/archived",
    async () => (await import("@/features/settings/pages/archived-page")).ArchivedPage,
    "purchases",
  ),
  page(
    "/settings/sign-in-way",
    async () => (await import("@/features/settings/pages/sign-in-way-page")).SignInWayPage,
    "owners",
  ),
  page(
    "/settings/pharmacy",
    async () => (await import("@/features/settings/pages/pharmacy-page")).PharmacyPage,
    "owners",
  ),
  page(
    "/settings/pharmacy/delete",
    async () => (await import("@/features/settings/pages/delete-pharmacy-page")).DeletePharmacyPage,
    "owners",
  ),
  page("/settings/terms", async () => (await import("@/features/settings/pages/terms-page")).TermsPage),

  page("/billing", async () => (await import("@/features/billing/pages/billing-page")).BillingPage, "owners"),
];

const routes: RouteObject[] = [
  {
    // A page that cannot open shows a plain message with Reload (RouteError),
    // not the router's technical one.
    errorElement: <RouteError />,
    children: [
      {
        element: <RequireSignedOut />,
        children: [
          {
            lazy: async () => ({ Component: (await import("@/components/layout/auth-layout")).AuthLayout }),
            children: publicPages,
          },
        ],
      },
      {
        element: <RequireSignedIn />,
        children: [
          {
            lazy: async () => ({ Component: (await import("@/components/layout/app-shell")).AppShell }),
            // The sale being made lives above every page: going elsewhere mid-sale loses nothing.
            children: [
              {
                // The plan: its notice on every page, and view only once it has ended.
                element: <PlanLayout />,
                children: [
                  { element: <DraftLayout />, children: [{ element: <TransferDraftLayout />, children: appPages }] },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  // The terms, from the sign-in pages' footer: for everybody, signed in or not.
  page("/terms", async () => (await import("@/features/auth/pages/terms-public-page")).TermsPublicPage),
  { path: "*", element: <NotFoundPage /> },
];

// The component gallery (Phase 3): in development only, never in the built app.
if (import.meta.env.DEV) {
  routes.unshift({
    path: "/dev/ui",
    lazy: async () => ({ Component: (await import("@/dev/gallery")).Gallery }),
  });
}

export function createRouter() {
  return createBrowserRouter(routes);
}

/** For tests: the same pages, in a memory router. */
export const appRoutes = routes;

/** This app's router, as app/active-router.ts names it. */
export const createAppRouter = createRouter;
